import { AnimatePresence, motion } from 'motion/react';
import { AlertCircle, CheckCircle2, ChevronDown, Loader2, Send, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/utils';
import { AdminMessage } from '../../lib/types';
import { Timestamp } from '../../firebase';

export interface SupportMessage {
  id: string;
  subject: string;
  message: string;
  status: string;
  createdAt: Timestamp | string;
}

interface ContactSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (subject: string, message: string) => Promise<void>;
  supportMessages: SupportMessage[];
  adminMessages: AdminMessage[];
  currentUserId?: string;
  onMarkAsRead: (id: string) => void;
}

export function ContactSupportModal({ isOpen, onClose, onSend, supportMessages, adminMessages, currentUserId, onMarkAsRead }: ContactSupportModalProps) {
  const [subject, setSubject] = useState('General Inquiry');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [activeTab, setActiveTab] = useState<'compose' | 'history' | 'communications'>('compose');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setMessage('');
      setSubject('General Inquiry');
      setActiveTab('compose');
      setExpandedId(null);
    }
  }, [isOpen]);

  const unreadCount = useMemo(() => {
    if (!currentUserId) return 0;
    return adminMessages.filter(msg => !msg.readBy?.includes(currentUserId)).length;
  }, [adminMessages, currentUserId]);

  const handleMessageClick = (msg: AdminMessage) => {
    const isExpanding = expandedId !== msg.id;
    setExpandedId(isExpanding ? msg.id : null);
    
    if (isExpanding && currentUserId && !msg.readBy?.includes(currentUserId)) {
      onMarkAsRead(msg.id);
    }
  };

  const handleTabClick = (tab: 'compose' | 'history' | 'communications') => {
    setActiveTab(tab);
    setExpandedId(null);
  };

  // Helper to safely format date from either String or Firestore Timestamp
  const formatDate = (dateInput: Timestamp | string | number | Date) => {
    if (!dateInput) return '';
    const date = dateInput instanceof Timestamp ? dateInput.toDate() : new Date(dateInput);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setStatus('sending');
    try {
      await onSend(subject, message);
      setStatus('success');
    } catch (error) {
      console.log('Error sending support message:', error);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-white dark:bg-black border border-zinc-100 dark:border-zinc-900 rounded-[32px] shadow-2xl overflow-hidden transition-colors flex flex-col max-h-[80vh]"
          >
            <div className="pt-6 px-6 pb-2 border-b border-zinc-100 dark:border-zinc-900">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Contact Support</h2>
                  <p className="text-xs text-zinc-500 font-medium">We're here to help</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors">
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>
              <div className="flex gap-6">
                <button onClick={() => handleTabClick('compose')} className={cn("pb-2 text-xs font-bold border-b-2 transition-colors", activeTab === 'compose' ? "border-zinc-900 dark:border-zinc-100 text-zinc-900 dark:text-zinc-100" : "border-transparent text-zinc-400 hover:text-zinc-600")}>
                  Compose
                </button>
                <button onClick={() => handleTabClick('history')} className={cn("pb-2 text-xs font-bold border-b-2 transition-colors", activeTab === 'history' ? "border-zinc-900 dark:border-zinc-100 text-zinc-900 dark:text-zinc-100" : "border-transparent text-zinc-400 hover:text-zinc-600")}>
                  History
                </button>
                <button onClick={() => handleTabClick('communications')} className={cn("pb-2 text-xs font-bold border-b-2 transition-colors", activeTab === 'communications' ? "border-zinc-900 dark:border-zinc-100 text-zinc-900 dark:text-zinc-100" : "border-transparent text-zinc-400 hover:text-zinc-600")}>
                  Communications
                  {unreadCount > 0 && (
                    <span className="ml-2 bg-rose-500 text-white text-[9px] px-1.5 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {activeTab === 'communications' ? (
                <div className="space-y-3">
                  {adminMessages.length === 0 ? (
                    <div className="text-center text-zinc-400 text-sm py-8 italic">No communications found.</div>
                  ) : (
                    adminMessages.map(msg => (
                      <motion.div layout key={msg.id} onClick={() => handleMessageClick(msg)} className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800 cursor-pointer overflow-hidden">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className={cn("text-zinc-900 dark:text-zinc-100 text-sm", !msg.readBy?.includes(currentUserId || '') ? "font-bold" : "font-medium")}>
                                {msg.title}
                              </h4>
                              {!msg.readBy?.includes(currentUserId || '') && (
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                              )}
                            </div>
                            <p className="text-[10px] text-zinc-400 font-medium">{formatDate(msg.createdAt)}</p>
                          </div>
                          <ChevronDown className={cn("w-4 h-4 text-zinc-400 transition-transform shrink-0 ml-4", expandedId === msg.id && "rotate-180")} />
                        </div>
                        <AnimatePresence>
                          {expandedId === msg.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <p className="text-xs text-zinc-600 dark:text-zinc-400 pt-2 border-t border-dashed border-zinc-200 dark:border-zinc-800 mt-2">
                                {msg.message}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </div>
              ) : activeTab === 'history' ? (
                <div className="space-y-3">
                  {supportMessages.length === 0 ? (
                    <div className="text-center text-zinc-400 text-sm py-8 italic">No support history found.</div>
                  ) : (
                    supportMessages.map(msg => (
                      <motion.div layout key={msg.id} onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)} className="p-4 rounded-2xl bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-100 dark:border-zinc-800 cursor-pointer overflow-hidden">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold text-zinc-900 dark:text-zinc-100 text-sm">{msg.subject}</h4>
                            <p className="text-[10px] text-zinc-400 font-medium">{formatDate(msg.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "text-[9px] px-2 py-1 rounded-full font-bold uppercase tracking-wider",
                              msg.status === 'resolved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                              msg.status === 'in_progress' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            )}>
                              {msg.status.replace('_', ' ')}
                            </span>
                            <ChevronDown className={cn("w-4 h-4 text-zinc-400 transition-transform shrink-0", expandedId === msg.id && "rotate-180")} />
                          </div>
                        </div>
                        <AnimatePresence>
                          {expandedId === msg.id && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <p className="text-xs text-zinc-600 dark:text-zinc-400 pt-2 border-t border-dashed border-zinc-200 dark:border-zinc-800 mt-2">
                                {msg.message}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))
                  )}
                </div>
              ) : status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
                  <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">Message Sent!</h3>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 max-w-xs mx-auto">
                      Thank you for contacting us. We've received your message and will get back to you shortly.
                    </p>
                  </div>
                  <button 
                    onClick={onClose}
                    className="mt-4 px-6 py-3 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl font-bold text-sm hover:opacity-90 transition-opacity"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {status === 'error' && (
                    <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/50 rounded-2xl flex items-center gap-3 text-rose-700 dark:text-rose-400">
                      <AlertCircle className="w-5 h-5 shrink-0" />
                      <p className="text-sm font-medium">Failed to send message. Please try again.</p>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Topic</label>
                    <select
                      className="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-100 border border-zinc-100 dark:border-zinc-900 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm appearance-none"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      disabled={status === 'sending'}
                    >
                      <option>General Inquiry</option>
                      <option>Report a Bug</option>
                      <option>Feature Request</option>
                      <option>Account Issue</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Message</label>
                    <textarea
                      required
                      rows={5}
                      placeholder="Describe your issue or question..."
                      className="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-950 dark:text-zinc-100 border border-zinc-100 dark:border-zinc-900 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all text-sm resize-none"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      disabled={status === 'sending'}
                    />
                  </div>

                  <button type="submit" disabled={status === 'sending'} className="w-full h-[48px] bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 shadow-lg active:scale-[0.98]">
                    {status === 'sending' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}